// The chat-home empty state per the gallery's chat-home section: the brand
// mark centred over the greeting, and preset action rows resting on the
// composer. Fully controlled — the harness owns visibility and each preset
// follows the same direct-send path as a typed message.
import { type ReactNode } from 'react';

import { type ComposerPreset } from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { FlowerMark } from '@apps/web/features/phoebe_chat_v3/ui/primitives/flower_mark';

export type ChatHomeStageProps = Readonly<{
    readonly greeting: string;
}>;

export function ChatHomeStage({ greeting }: ChatHomeStageProps): ReactNode {
    return (
        <div className="flex h-full flex-col items-center justify-center px-5 text-center">
            <FlowerMark className="text-agent-action w-[52px] dark:text-violet-300" />
            <h2 className="m-0 mt-4 text-[22px] leading-[29px] font-semibold tracking-[-0.02em]">
                {greeting}
            </h2>
        </div>
    );
}

export type ChatHomePresetRowsProps = Readonly<{
    readonly disabled?: boolean;
    readonly presets: ReadonlyArray<ComposerPreset>;
    readonly onSelect: (preset: ComposerPreset) => void;
}>;

// No trailing chevron on the rows: nothing promises a next step, so the row
// stays honest whether it sends a sentence or later opens a flow.
export function ChatHomePresetRows({
    disabled = false,
    presets,
    onSelect,
}: ChatHomePresetRowsProps): ReactNode {
    return (
        <div className="mb-2.5">
            {presets.map((preset) => (
                <button
                    key={preset.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => onSelect(preset)}
                    className="group hover:bg-muted focus-visible:outline-agent-action flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left transition-colors duration-[120ms] ease-[cubic-bezier(0.22,1,0.36,1)] outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-[0.42] motion-reduce:transition-none"
                >
                    <span className="text-muted-foreground grid size-5 shrink-0 place-items-center">
                        <preset.icon aria-hidden="true" className="size-4" />
                    </span>
                    <span className="text-foreground/80 group-hover:text-foreground text-v3-chat min-w-0 font-semibold tracking-[-0.01em] [overflow-wrap:anywhere]">
                        {preset.label}
                    </span>
                </button>
            ))}
        </div>
    );
}
